import { cellKey, cellsOf, haloOf, inBounds, neighbours8 } from './placement.js';
import { totalShipCells } from './ruleset.js';
/** Zbiór kluczy pól, w które już strzelano na danej planszy. */
export function shotKeys(shots, boardSize) {
    const keys = new Set();
    for (const s of shots)
        keys.add(cellKey(s.x, s.y, boardSize));
    return keys;
}
/**
 * Rozstrzyga strzał w planszę o podanej flocie.
 *
 * `alreadyShot` to pola ostrzelane PRZED tym strzałem — funkcja nie sprawdza,
 * czy pole nie było już ostrzelane (to zadanie maszyny stanów), tylko czy trafia
 * i czy trafienie kończy statek.
 */
export function resolveShot(fleet, alreadyShot, target, ruleset) {
    const { boardSize } = ruleset;
    const targetKey = cellKey(target.x, target.y, boardSize);
    const hitShip = fleet.find((ship) => cellsOf(ship).some((c) => cellKey(c.x, c.y, boardSize) === targetKey));
    if (!hitShip)
        return { result: 'miss', sunkCells: [], revealed: [] };
    const cells = cellsOf(hitShip);
    const sunk = cells.every((c) => cellKey(c.x, c.y, boardSize) === targetKey || alreadyShot.has(cellKey(c.x, c.y, boardSize)));
    if (!sunk)
        return { result: 'hit', shipId: hitShip.id, sunkCells: [], revealed: [] };
    const revealed = ruleset.autoRevealHaloOnSunk
        ? haloOf(hitShip, boardSize).filter((c) => !alreadyShot.has(cellKey(c.x, c.y, boardSize)))
        : [];
    return { result: 'sunk', shipId: hitShip.id, sunkCells: cells, revealed };
}
/** Czy statek o podanym id jest już w całości trafiony. */
export function isShipSunk(fleet, shipId, hitKeys, boardSize) {
    const ship = fleet.find((s) => s.id === shipId);
    if (!ship)
        return false;
    return cellsOf(ship).every((c) => hitKeys.has(cellKey(c.x, c.y, boardSize)));
}
/** Ile pól floty zostało trafionych. */
export function hitCount(shots) {
    return shots.filter((s) => s.result === 'hit' || s.result === 'sunk').length;
}
/** Czy cała flota jest zatopiona — warunek zwycięstwa. */
export function isFleetDestroyed(shots, ruleset) {
    return hitCount(shots) >= totalShipCells(ruleset.roster);
}
/** Pola, w które jeszcze nie strzelano — używane przy strzale losowym po przekroczeniu czasu. */
export function remainingTargets(shots, boardSize) {
    const taken = shotKeys(shots, boardSize);
    const out = [];
    for (let y = 0; y < boardSize; y++) {
        for (let x = 0; x < boardSize; x++) {
            if (!taken.has(cellKey(x, y, boardSize)))
                out.push({ x, y });
        }
    }
    return out;
}
/**
 * Kształty zatopionych statków odtworzone z samych strzałów — to, co gracz i tak już wie
 * o planszy przeciwnika w trakcie partii (pełna flota przychodzi dopiero po jej końcu).
 * Działa, bo statki nie mogą się stykać (`TOUCHING` w `validateFleet`): spójny, w pionie
 * i poziomie, zbiór trafień zawierający strzał `sunk` to dokładnie jeden statek.
 * Id bierzemy ze strzału `sunk`, więc kreska kadłuba nie zmienia się, gdy po końcu partii
 * ten sam statek przyjdzie już jako część prawdziwej floty.
 */
export function sunkShipsFromShots(shots, boardSize) {
    const hits = hitKeys(shots, boardSize);
    const ships = [];
    shots.forEach((shot, index) => {
        if (shot.result !== 'sunk')
            return;
        const cells = connectedHits(shot, hits, boardSize);
        // Więcej pól niż największy statek znaczyłoby niespójne dane — lepiej nic nie rysować.
        if (cells.length > 4)
            return;
        const x = Math.min(...cells.map((c) => c.x));
        const y = Math.min(...cells.map((c) => c.y));
        const horizontal = cells.every((c) => c.y === y);
        ships.push({
            id: shot.shipId ?? index,
            size: cells.length,
            x,
            y,
            dir: horizontal ? 'h' : 'v',
        });
    });
    return ships;
}
function hitKeys(shots, boardSize) {
    return new Set(shots
        .filter((s) => s.result === 'hit' || s.result === 'sunk')
        .map((s) => cellKey(s.x, s.y, boardSize)));
}
/** Trafienia połączone (w pionie i poziomie) z polem `start` — przy niestykających się statkach to jeden statek. */
function connectedHits(start, hits, boardSize) {
    const cells = [];
    const seen = new Set([cellKey(start.x, start.y, boardSize)]);
    const queue = [{ x: start.x, y: start.y }];
    while (queue.length > 0) {
        const cell = queue.shift();
        cells.push(cell);
        for (const [dx, dy] of [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
        ]) {
            const x = cell.x + dx;
            const y = cell.y + dy;
            if (!inBounds(x, y, boardSize))
                continue;
            const key = cellKey(x, y, boardSize);
            if (seen.has(key) || !hits.has(key))
                continue;
            seen.add(key);
            queue.push({ x, y });
        }
    }
    return cells;
}
/**
 * Pola ostatniej serii strzałów w tę planszę, w kolejności oddania — do oznaczenia
 * „ostatniego ruchu” na planszy. Liczone z samej listy strzałów (także ze snapshotu po
 * odświeżeniu strony), bez śledzenia zdarzeń:
 *
 *  - pudła odkryte automatycznie wokół zatopionego statku (`autoRevealHaloOnSunk`) leżą
 *    w liście zaraz po strzale `sunk` i nie są niczyim ruchem, więc je pomijamy; prawdziwe
 *    pudło nie może trafić w aureolę, bo ta jest już w całości odkryta;
 *  - tura kończy się tylko pudłem (`extraShotOnHit`), więc seria to ostatni strzał plus
 *    wszystkie bezpośrednio poprzedzające go trafienia.
 */
export function lastVolley(shots, ruleset) {
    const size = ruleset.boardSize;
    const auto = new Set();
    if (ruleset.autoRevealHaloOnSunk) {
        const hits = hitKeys(shots, size);
        shots.forEach((shot, index) => {
            if (shot.result !== 'sunk')
                return;
            // Sąsiedzi (także po skosie) pól statku — pola samego statku to trafienia, nie pudła,
            // więc nie trzeba ich odejmować.
            const halo = new Set(connectedHits(shot, hits, size).flatMap((cell) => neighbours8(cell, size).map((c) => cellKey(c.x, c.y, size))));
            for (let k = index + 1; k < shots.length; k++) {
                const next = shots[k];
                if (next.result !== 'miss' || !halo.has(cellKey(next.x, next.y, size)))
                    break;
                auto.add(k);
            }
        });
    }
    const real = shots.filter((_, index) => !auto.has(index));
    const last = real.at(-1);
    if (!last)
        return [];
    if (!ruleset.extraShotOnHit)
        return [{ x: last.x, y: last.y }];
    const volley = [{ x: last.x, y: last.y }];
    for (let i = real.length - 2; i >= 0; i--) {
        const shot = real[i];
        if (shot.result === 'miss')
            break;
        volley.unshift({ x: shot.x, y: shot.y });
    }
    return volley;
}
//# sourceMappingURL=shooting.js.map